/**
 * Pre-written LinkedIn Templates by Plan Type (F-184 / T-141-001)
 *
 * High-converting, compliance-focused post copy segmented across:
 * - Independent (1-4 units, self-managing, time-poor)
 * - Essentials (Single-family & small portfolios needing foundational automation)
 * - Professional (5-20 units, multi-property scaling, eviction defense & contractor workflows)
 * - Enterprise (21+ units, property management agencies, brokerage SSO, audit trails)
 */

import { PostTemplate, PostPlanTier } from './types';
import { getMeetingLinkUrl } from '../../config/meeting-link';

export const LINKEDIN_PLAN_LABELS: Record<PostPlanTier, { name: string; target: string; badge: string }> = {
  independent: {
    name: 'Independent Landlord',
    target: '1–4 units • DIY Landlords',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  essentials: {
    name: 'Essentials Plan',
    target: 'Small Portfolios • Fast Automation',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  professional: {
    name: 'Professional Plan',
    target: '5–20 units • Scaling Portfolios',
    badge: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  },
  enterprise: {
    name: 'Enterprise & Agency',
    target: '20+ units • Property Managers & Brokers',
    badge: 'bg-purple-50 text-purple-700 border-purple-200',
  },
};

export function getFullPostText(tpl: PostTemplate): string {
  const meetingUrl = getMeetingLinkUrl({ tier: tpl.planType, campaign: 'linkedin-template-library' });
  return [
    tpl.hook,
    '',
    tpl.painPoint,
    '',
    tpl.valueProp,
    '',
    `${tpl.cta}: ${meetingUrl}`,
    '',
    tpl.tags.map((t) => (t.startsWith('#') ? t : `#${t}`)).join(' '),
  ].join('\n');
}

export const PREWRITTEN_TEMPLATES: PostTemplate[] = [
  // ── INDEPENDENT ──────────────────────────────────────────────────────────
  {
    id: 'ind-001',
    planType: 'independent',
    title: 'The Weekend Landlord’s Paperwork Trap',
    hook: 'Are you spending your Sunday evenings matching bank transfers to Excel rows?',
    painPoint: 'When you manage 1 to 4 rental properties yourself, every compliance change—from municipal registrations to 7-day rekeying rules—feels like a second unpaid job.',
    valueProp: 'Independent landlords use automated compliance tracking to cut weekly admin by 80%. Digital receipt vault, statutory deadline monitors, and one-click lease disclosures in your pocket.',
    cta: 'Grab your 15-day free trial or book a quick 10-min walkthrough',
    tags: ['TexasLandlord', 'RealEstateInvesting', 'PropertyManagement', 'PropTech'],
  },
  {
    id: 'ind-002',
    planType: 'independent',
    title: 'Texas Subchapter D: The Rekeying Countdown',
    hook: 'Did you know Texas Property Code mandates lock rekeying within 7 days of turnover?',
    painPoint: 'A missed rekeying deadline can leave independent landlords liable for statutory damages up to $500 plus one month’s rent and attorney fees.',
    valueProp: 'Our Texas Compliance Engine automatically starts the 7-day countdown when tenant move-out is marked, generates the contractor dispatch work-order, and archives proof of compliance.',
    cta: 'See how self-managing landlords protect their portfolio',
    tags: ['TexasPropertyCode', 'SubchapterD', 'LandlordCompliance', 'AustinRealEstate'],
  },

  // ── ESSENTIALS ───────────────────────────────────────────────────────────
  {
    id: 'ess-001',
    planType: 'essentials',
    title: 'Never Miss an HVAC Seasonal Readiness Inspection',
    hook: 'Summer heat in Texas is unforgiving. So are municipal city inspectors.',
    painPoint: 'Cities like Dallas, Houston, and San Antonio are stepping up municipal habitability audits. One tenant complaint without a certified HVAC service log can lead to hefty stop-rent orders.',
    valueProp: 'Essentials Plan tracks seasonal HVAC readiness, sends proactive contractor booking reminders, and stores certified inspection receipts tied directly to each unit record.',
    cta: 'Explore Essentials features with an expert',
    tags: ['TexasRealEstate', 'HVACReadiness', 'DallasLandlords', 'HoustonRentals'],
  },
  {
    id: 'ess-002',
    planType: 'essentials',
    title: 'Security Deposit Disputes Are Won or Lost in the First 30 Days',
    hook: 'Texas law gives landlords 30 days to return or itemize security deposits. 31 days is too late.',
    painPoint: 'Under Texas Property Code § 92.109, bad-faith retention presumes liability for 3x the deposit amount plus $100 and attorney fees.',
    valueProp: 'Automate deposit itemization timelines, photo evidence stamping, and certified mailing delivery tracking with our dedicated Texas Deposit Ledger.',
    cta: 'Schedule a free 15-minute consultation',
    tags: ['SecurityDeposits', 'TexasLaw', 'LandlordTips', 'RealEstateTech'],
  },

  // ── PROFESSIONAL ─────────────────────────────────────────────────────────
  {
    id: 'pro-001',
    planType: 'professional',
    title: 'Scaling from 5 to 20 Units Without Hiring an Admin',
    hook: 'Managing 15 properties shouldn’t require a full-time compliance clerk.',
    painPoint: 'As portfolios grow, juggling multiple city registrations (Dallas Ch. 27, Houston Ch. 10, San Antonio HRRB) becomes an operational bottleneck that slows acquisition velocity.',
    valueProp: 'Professional tier brings centralized multi-property dashboards, batch notice generation (Texas SB38), team seats, and 20 monthly on-demand service allowances.',
    cta: 'Book a demo of our Professional tier for scaling operators',
    tags: ['MultifamilyTexas', 'PropertyManagement', 'RealEstateScale', 'DFWRealEstate'],
  },
  {
    id: 'pro-002',
    planType: 'professional',
    title: 'The Eviction Documentation Chain: Don’t Let a Flaw Delay Possession',
    hook: 'One technical error on a Notice to Vacate can reset a 6-week justice court process back to day one.',
    painPoint: 'Texas Justice Courts strictly scrutinize statutory delivery methods, cure periods, and CARES Act / SB38 disclosures. Incomplete notice evidence causes dismissed cases.',
    valueProp: 'The Professional Eviction Pack generates ironclad Notice to Vacate packs with certified mail tracking, delivery attestations, and timeline calculations that withstand court scrutiny.',
    cta: 'See our legal notice chain in action',
    tags: ['TexasEvictions', 'JusticeCourt', 'NoticeToVacate', 'PropertyLaw'],
  },

  // ── ENTERPRISE ───────────────────────────────────────────────────────────
  {
    id: 'ent-001',
    planType: 'enterprise',
    title: 'Enterprise SSO & Role-Based Access for Commercial Property Managers',
    hook: 'Is your brokerage still sharing spreadsheet logins for property compliance?',
    painPoint: 'Property management enterprises managing hundreds of doors cannot risk decentralized credential sharing, unvetted contractor access, or fragmented audit trails across regional offices.',
    valueProp: 'Enterprise provides Okta and Azure AD SAML 2.0 Single Sign-On, custom role permissions, immutable audit logging, multi-city oversight, and dedicated account management.',
    cta: 'Request an Enterprise SSO architecture review & custom demo',
    tags: ['EnterpriseSaaS', 'PropTechEnterprise', 'OktaSSO', 'AzureAD', 'RealEstateOps'],
  },
  {
    id: 'ent-002',
    planType: 'enterprise',
    title: 'Multi-City Texas Portfolio Governance from a Single Cockpit',
    hook: 'How do you keep 500 units across Dallas, Houston, Austin, and San Antonio 100% audit-ready?',
    painPoint: 'Each Texas municipality enforces distinct rental registration, lead paint disclosure, and habitability codes. Missing one ordinance across dozens of entities creates severe municipal exposure.',
    valueProp: 'Enterprise delivers automated API sync with municipal registration portals, portfolio-wide risk scoring, SCIM team provisioning, and continuous statutory compliance monitoring.',
    cta: 'Book a 1:1 executive consultation with our Texas compliance team',
    tags: ['CommercialRealEstate', 'TexasEnterprise', 'PropTech', 'AssetManagement'],
  },
];
